import {
  useAddresses,
  useCreateAddress,
  useUpdateAddress
} from "@/queries/customer/addresses";
import { useProfile } from "@/queries/customer/customer";
import { handleActionError } from "@/lib/errors";
import { useCallback, useMemo, useState } from "react";
import { toAddressInput } from "./address-input";
import type { AddressFormData } from "./address-schema";
import { DEFAULT_ADDRESS_NAME, MAKE_DEFAULT } from "./default-address";

export interface UseDefaultAddressStepOptions {
  /**
   * Names the surface in error reports ("onboarding", "external checkout"):
   * the failures are logged as `<boundary> address`, `<boundary> choose
   * address` and `<boundary> continue`.
   */
  boundary: string;
  /**
   * Runs once the address is saved as the default shipping and billing address.
   * If it throws, the address stays saved: the step shows the error and falls
   * back to offering that address as a choice, so a retry never saves a
   * duplicate.
   */
  onSaved: () => void | Promise<void>;
}

/**
 * The state behind "give us the delivery address we should default to", shared
 * by the first-login onboarding page and the external-checkout address dialog.
 *
 * Two shapes, by what the buyer already has:
 * - **No saved address**: the address form. A first address is not asked for a
 *   nickname.
 * - **Saved addresses, none the default**: pick one of them rather than typing
 *   it again. "Add a new address" (`adding`) is still there for a buyer whose
 *   delivery address really is new.
 *
 * Either way the address ends up as both the default shipping and default
 * billing address, which is what both checkouts read.
 */
export function useDefaultAddressStep({
  boundary,
  onSaved
}: UseDefaultAddressStepOptions) {
  const { data: profile, isLoading: profileLoading } = useProfile();
  const {
    data: addressData,
    isLoading: addressesLoading,
    isError: addressesError,
    refetch: refetchAddresses
  } = useAddresses();
  const { mutateAsync: createAddress, isPending: isCreating } =
    useCreateAddress();
  const { mutateAsync: updateAddress, isPending: isUpdating } =
    useUpdateAddress();

  const [serverError, setServerError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [pickedId, setPickedId] = useState<string | null>(null);

  // Whether the buyer had saved addresses at the moment of saving. Saving
  // invalidates the address list, so without the snapshot the step would flip
  // from "no addresses: the form" to "one address: the choice" for the moment
  // before the caller moves on.
  const [hadAddressesAtSave, setHadAddressesAtSave] = useState<boolean | null>(
    null
  );

  const addresses = useMemo(() => addressData?.addresses ?? [], [addressData]);
  const hasAddresses = hadAddressesAtSave ?? addresses.length > 0;
  const showForm = !hasAddresses || adding;

  // Pre-select the billing address if there is one - the buyer has already
  // told us that one is theirs - else the first, so the common case is one tap.
  const chosenId =
    pickedId ??
    addresses.find((address) => address.is_default_billing)?.id ??
    addresses[0]?.id ??
    null;

  const customer = profile?.customer;
  // The form reads its defaults once, so render it only after the profile has
  // arrived (`isLoading`).
  const defaultValues = {
    address_name: hasAddresses ? "" : DEFAULT_ADDRESS_NAME,
    first_name: customer?.first_name ?? "",
    last_name: customer?.last_name ?? "",
    company: customer?.company_name ?? "",
    phone: customer?.phone ?? ""
  };

  const finish = useCallback(async () => {
    try {
      await onSaved();
    } catch (error) {
      // The address is saved but the caller could not move on. Dropping the
      // at-save snapshot shows the saved address as a choice, so the retry is
      // "use this address" rather than a form that would save a duplicate.
      setHadAddressesAtSave(null);
      setServerError(
        handleActionError(error, {
          boundary: `${boundary} continue`,
          fallback:
            "Your address is saved, but we couldn't continue. Please try again."
        })
      );
    }
  }, [boundary, onSaved]);

  const handleCreate = useCallback(
    async (data: AddressFormData) => {
      setServerError(null);
      setHadAddressesAtSave(hasAddresses);
      try {
        await createAddress({
          // A buyer who is choosing between addresses is asked to name the new
          // one; a first-address buyer is not.
          address_name: hasAddresses
            ? data.address_name
            : data.company?.trim() || DEFAULT_ADDRESS_NAME,
          ...toAddressInput(data),
          ...MAKE_DEFAULT
        });
      } catch (error) {
        setHadAddressesAtSave(null);
        setServerError(
          handleActionError(error, {
            boundary: `${boundary} address`,
            fallback: "We couldn't save your address. Please try again."
          })
        );
        return;
      }
      await finish();
    },
    [boundary, createAddress, finish, hasAddresses]
  );

  const handleChoose = useCallback(async () => {
    if (!chosenId) return;
    setServerError(null);
    try {
      await updateAddress({ addressId: chosenId, address: MAKE_DEFAULT });
    } catch (error) {
      setServerError(
        handleActionError(error, {
          boundary: `${boundary} choose address`,
          fallback: "We couldn't use that address. Please try again."
        })
      );
      return;
    }
    await finish();
  }, [boundary, chosenId, finish, updateAddress]);

  const startAdding = useCallback(() => {
    setServerError(null);
    setAdding(true);
  }, []);

  /** Back from the add-new form to the choice list. */
  const stopAdding = useCallback(() => {
    setServerError(null);
    setAdding(false);
  }, []);

  return {
    isLoading: profileLoading || addressesLoading,
    /** The address list could not be loaded (never "no addresses"). */
    addressesError,
    retryAddresses: refetchAddresses,
    customer,
    addresses,
    hasAddresses,
    showForm,
    defaultValues,
    chosenId,
    setPickedId,
    serverError,
    isCreating,
    isUpdating,
    handleCreate,
    handleChoose,
    startAdding,
    stopAdding
  };
}
